import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, assetUrl, errorText, useFetch } from "../api";
import { Alerts, Badge, ConfirmDialog, EmptyRow, Facts, Loading, Page, Pager, Photo, ReasonDialog, Tabs, UserLink } from "../components/ui";
import { label, money, PAGE_SIZE, qs, setQuery, text, when } from "../format";

function DepositActions({ id, onDone }) {
  const [mode, setMode] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState({ message: "", errors: {} });

  function close() {
    if (busy) return;
    setMode("");
    setProblem({ message: "", errors: {} });
  }

  async function approve() {
    setBusy(true);
    setProblem({ message: "", errors: {} });
    try {
      await api("/deposits/" + id + "/approve", { method: "POST" });
      setMode("");
      onDone("Deposit approved");
    } catch (err) {
      setProblem({ message: errorText(err), errors: err.errors || {} });
    } finally {
      setBusy(false);
    }
  }

  async function reject(reason) {
    setBusy(true);
    setProblem({ message: "", errors: {} });
    try {
      await api("/deposits/" + id + "/reject", { method: "POST", body: { reason } });
      setMode("");
      onDone("Deposit rejected");
    } catch (err) {
      setProblem({ message: errorText(err), errors: err.errors || {} });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row-actions" onClick={(event) => event.stopPropagation()}>
      <button type="button" className="btn small" onClick={() => setMode("approve")}>Approve</button>
      <button type="button" className="btn danger small" onClick={() => setMode("reject")}>Reject</button>
      {mode === "approve" ? (
        <ConfirmDialog
          title="Approve deposit"
          text="This adds the amount to the member's single USDT wallet and emails the member. The balance is not stored on the user document. A second click on a reviewed deposit returns an error."
          error={problem.message}
          busy={busy}
          confirmLabel="Approve"
          onClose={close}
          onConfirm={approve}
        />
      ) : null}
      {mode === "reject" ? (
        <ReasonDialog
          title="Reject deposit"
          label="Reason"
          hint="The wallet does not change."
          error={problem.errors.reason || problem.message}
          busy={busy}
          max={300}
          onClose={close}
          onSubmit={reject}
        />
      ) : null}
    </div>
  );
}

export default function Deposits() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const status = params.get("status") || "pending";
  const list = useFetch("/deposits" + qs({ page, limit: PAGE_SIZE, status }));
  const rows = Array.isArray(list.data?.data) ? list.data.data : [];
  const [notice, setNotice] = useState("");

  return (
    <Page title="Deposits" text="Approve adds the amount to the member's single USDT wallet and emails the member. The balance is not stored on the user document. On-chain BEP20 rows are on BEP20 deposits.">
      <Alerts error={list.error} notice={notice} />
      <Tabs
        value={status}
        onChange={(value) => {
          setNotice("");
          setQuery(params, setParams, { status: value, page: "" });
        }}
        options={[
          { value: "pending", label: "Pending" },
          { value: "approved", label: "Approved" },
          { value: "rejected", label: "Rejected" },
        ]}
      />
      {list.loading ? <Loading /> : (
        <div className="table-wrap">
          <table className="wide">
            <thead>
              <tr>
                <th>Member</th><th className="num">Amount</th><th>Method</th><th>Reference</th><th>Status</th><th>Submitted</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row) => (
                <tr key={row.id} className="click" onClick={() => navigate("/admin/deposits/" + row.id)}>
                  <td onClick={(event) => event.stopPropagation()}><UserLink user={row.user} userId={row.userId} /></td>
                  <td className="num">{money(row.amount)}</td>
                  <td>{label(row.method)}</td>
                  <td className="mono">{text(row.txHash)}</td>
                  <td><Badge value={row.status} /></td>
                  <td>{when(row.createdAt)}</td>
                  <td>
                    {row.status === "pending" ? <DepositActions id={row.id} onDone={(message) => { setNotice(message); list.reload(); }} /> : text(row.reason)}
                  </td>
                </tr>
              )) : <EmptyRow cols={7} text="No deposits" />}
            </tbody>
          </table>
        </div>
      )}
      {list.data ? (
        <Pager page={page} limit={PAGE_SIZE} total={list.data.total} onPage={(next) => setQuery(params, setParams, { page: next <= 1 ? "" : next })} />
      ) : null}
    </Page>
  );
}

export function DepositDetail() {
  const { id } = useParams();
  const { data, loading, error, reload } = useFetch("/deposits/" + id);
  const row = data?.data;
  const [notice, setNotice] = useState("");

  return (
    <Page title="Deposit">
      <Link className="back" to="/admin/deposits">Back to deposits</Link>
      <Alerts error={error} notice={notice} />
      {loading ? <Loading /> : null}
      {row ? (
        <div className="panel">
          {row.proofImage ? (
            <a href={assetUrl(row.proofImage)} target="_blank" rel="noreferrer">
              <Photo src={row.proofImage} className="proof" alt="Payment screenshot" />
            </a>
          ) : <p className="hint">No screenshot</p>}
          <Facts items={[
            { label: "Amount", value: money(row.amount) },
            { label: "Status", value: <Badge value={row.status} /> },
            { label: "Method", value: label(row.method) },
            { label: "Reference", value: <span className="mono">{text(row.txHash)}</span> },
            { label: "Member", value: <UserLink user={row.user} userId={row.userId} /> },
            { label: "Submitted", value: when(row.createdAt) },
            { label: "Reviewed", value: when(row.reviewedAt) },
            { label: "Reason", value: text(row.reason) },
          ]} />
          {row.status === "pending" ? (
            <div className="block-gap">
              <DepositActions id={row.id} onDone={(message) => { setNotice(message); reload(); }} />
            </div>
          ) : null}
        </div>
      ) : null}
    </Page>
  );
}
